<!-- PersonalityResults.vue -->
<template>
  <div class="personality-results">
    <h3 style="color:black">Ecco i risultati del tuo test di personalità</h3>
    <div class="traits-container">
      <TransitionGroup name="trait-list">
        <div v-for="(trait, key) in traits"
             :key="key"
             class="trait-card"
             :class="{ 'trait-card--active': selectedTrait === key }"
             :style="{
               '--trait-color': getBackgroundColor(key, 1),
               '--trait-color-light': getBackgroundColor(key, 0.15),
               '--trait-color-medium': getBackgroundColor(key, 0.3)
             }"
             @click="toggleTrait(key)"
             role="button"
             tabindex="0"
             :aria-expanded="selectedTrait === key"
             @keydown.enter="toggleTrait(key)"
             @keydown.space.prevent="toggleTrait(key)">

          <!-- Header Section -->
          <div class="trait-header">
            <div class="trait-title">
              <i :class="getTraitIcon(key)" class="trait-icon" :aria-label="trait.title"></i>
              <h3>{{ trait.title }}</h3>
            </div>
            <div class="trait-score">
              <i class="fas fa-chevron-down chevron-icon"
                 :class="{ 'chevron-icon--rotated': selectedTrait === key }"
                 aria-hidden="true"></i>
            </div>
          </div>

          <!-- Progress Section -->
          <div class="progress-section">
            <span class="progress-label">{{ trait.leftLabel }}</span>
            <div class="progress-bar"
                 role="progressbar"
                 :aria-valuenow="scores[key]"
                 aria-valuemin="0"
                 aria-valuemax="100">
              <div class="progress-bar__background">
                <div class="progress-bar__fill"
                     :style="{ width: scores[key] + '%' }">
                </div>
              </div>
            </div>
            <span class="progress-label progress-label--right">{{ trait.rightLabel }}</span>
          </div>

          <!-- Expanded Content -->
          <Transition name="expand">
            <div v-if="selectedTrait === key"
                 class="expanded-content"
                 :style="{ borderColor: getBackgroundColor(key, 0.3) }">
              <p class="trait-description">{{ trait.description }}</p>
              <p class="trait-analysis">{{ getDescription(key, scores[key]) }}</p>
            </div>
          </Transition>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PersonalityResults',
  props: {
    initialScores: {
      type: Object,
      required: true,
      validator(value) {
        const requiredTraits = ['openness', 'conscientiousness', 'extraversion', 'agreeableness', 'neuroticism'];
        return requiredTraits.every(trait =>
            typeof value[trait] === 'number' &&
            value[trait] >= 0 &&
            value[trait] <= 100
        );
      }
    }
  },
  data() {
    return {
      selectedTrait: null,
      scores: { ...this.initialScores },
      traits: {
        openness: {
          title: "Curiosità e Avventura",
          description: "Quanto ti piace scoprire cose nuove e vivere avventure.",
          leftLabel: "Abitudinario/a",
          rightLabel: "Esplorativo/a",
          low: "Ti piacciono le cose familiari e sei bravo/a a seguire le tradizioni, come preparare i biscotti con la ricetta della nonna.",
          medium: "A volte ti piace provare cose nuove, altre volte preferisci fare quello che conosci già bene - come alternare tra il tuo gioco preferito e uno nuovo.",
          high: "Sei sempre pronto/a per una nuova avventura e ami imparare cose nuove, proprio come un/una giovane esploratore/trice!"
        },
        conscientiousness: {
          title: "Organizzazione",
          description: "Come ti piace organizzare le tue attività e i tuoi compiti.",
          leftLabel: "Spontaneo/a",
          rightLabel: "Organizzato/a",
          low: "Sei bravo/a ad adattarti ai cambiamenti e ti diverti a fare le cose in modo spontaneo, come inventare giochi al momento!",
          medium: "Sai essere organizzato/a quando serve ma anche flessibile - come quando organizzi il tuo tempo per studiare e fare i compiti, lasciando però sempre un po' di spazio libero per uscire con gli amici all'ultimo minuto.",
          high: "Ti piace avere tutto in ordine e organizzato, proprio come un regista che prepara ogni scena nei minimi dettagli prima di girare un film!"
        },
        extraversion: {
          title: "Amicizia e Compagnia",
          description: "Come ti piace passare il tempo con gli altri.",
          leftLabel: "Riservato/a",
          rightLabel: "Socievole",
          low: "Ti piace stare con pochi amici speciali e fare attività tranquille, come guardare un film o disegnare insieme!",
          medium: "A volte ti piace giocare in gruppo, altre volte preferisci stare tranquillo/a - come alternare una partita di calcio con un bel libro!",
          high: "Ami stare in mezzo agli amici e fare tante attività insieme, come organizzare giochi di squadra!"
        },
        agreeableness: {
          title: "Spirito di Squadra",
          description: "Come ti comporti quando lavori con gli altri.",
          leftLabel: "Indipendente",
          rightLabel: "Collaborativo/a",
          low: "Sei bravo/a a pensare con la tua testa e a risolvere i problemi da solo/a, senza chiedere aiuto.",
          medium: "Sai quando è meglio lavorare da solo/a e quando in gruppo.",
          high: "Ami aiutare gli altri e lavorare insieme, proprio come una band di musicisti che suona in perfetta armonia!"
        },
        neuroticism: {
          title: "Mondo delle Emozioni",
          description: "Come vivi e esprimi i tuoi sentimenti.",
          leftLabel: "Razionale",
          rightLabel: "Emotivo/a",
          low: "Sei bravo a mantenere la calma e ad affrontare le sfide con tranquillità, come un saggio maestro di kung fu!",
          medium: "Sai gestire bene le tue emozioni - come un/una capitano/a che guida la sua nave sia con il mare calmo che nella tempesta!",
          high: "Sei molto attento/a ai sentimenti e vivi le emozioni intensamente, come un/una artista che dipinge con tutti i colori!"
        }
      }
    }
  },
  methods: {
    getDescription(trait, score) {
      if (score <= 35) return this.traits[trait].low;
      if (score <= 65) return this.traits[trait].medium;
      return this.traits[trait].high;
    },
    toggleTrait(trait) {
      this.selectedTrait = this.selectedTrait === trait ? null : trait;
    },
    getTraitIcon(trait) {
      const icons = {
        openness: 'fas fa-lightbulb',
        conscientiousness: 'fas fa-tasks',
        extraversion: 'fas fa-users',
        agreeableness: 'fas fa-heart',
        neuroticism: 'fas fa-brain'
      };
      return icons[trait];
    },
    getBackgroundColor(trait, opacity) {
      const colorMap = {
        openness: `rgba(147, 51, 234, ${opacity})`,      // Purple
        conscientiousness: `rgba(59, 130, 246, ${opacity})`, // Blue
        extraversion: `rgba(234, 179, 8, ${opacity})`,      // Yellow
        agreeableness: `rgba(239, 68, 68, ${opacity})`,     // Red
        neuroticism: `rgba(34, 197, 94, ${opacity})`        // Green
      };
      return colorMap[trait];
    }
  }
}
</script>

<style scoped>
.personality-results {
  width: 100%;
  margin: 0 auto;
  padding: 2rem;
  background-color: #F9FAFB;
}

.traits-container {
  position: relative;
}

.trait-card {
  background-color: var(--trait-color-light);
  border: 2px solid var(--trait-color-medium);
  border-radius: 1.5rem;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  cursor: pointer;
  transition: transform 0.3s ease;
  position: relative;
  overflow: hidden;
  background-clip: padding-box;
}

.trait-card--active {
  position: relative;
  z-index: 1;
}

.trait-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.trait-card--active {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.trait-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.trait-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.trait-icon {
  color: var(--trait-color);
  font-size: 1.25rem;
}

.trait-title h3 {
  color: var(--trait-color);
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.trait-score {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.score-value {
  color: #4B5563;
  font-weight: 600;
}

.chevron-icon {
  color: var(--trait-color);
  transition: transform 0.3s ease;
}

.chevron-icon--rotated {
  transform: rotate(180deg);
}

.progress-section {
  display: grid;
  grid-template-columns: 100px 1fr 100px;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0;
}

.progress-label {
  color: #4B5563;
  font-size: 0.875rem;
  font-weight: 500;
}

.progress-label--right {
  text-align: right;
}

.progress-bar {
  width: 100%;
}

.progress-bar__background {
  background-color: var(--trait-color-light);
  height: 12px;
  border-radius: 999px;
  overflow: hidden;
}

.progress-bar__fill {
  background-color: var(--trait-color);
  height: 100%;
  border-radius: 999px;
  transition: width 1.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.expanded-content {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid var(--trait-color-medium);
}

.trait-description {
  color: #4B5563;
  margin-bottom: 1rem;
}

.trait-analysis {
  color: #1F2937;
  font-weight: 500;
}

/* Transitions */
.expand-enter-active,
.expand-leave-active {
  transition: max-height 0.3s ease-out, opacity 0.2s ease-out;
  max-height: 500px; /* Adjust based on your maximum content height */
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.trait-list-move {
  transition: transform 0.3s ease-out;
}

.trait-list-enter-active,
.trait-list-leave-active {
  transition: all 0.3s ease-out;
}

.trait-list-enter-from,
.trait-list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

@media (max-width: 640px) {
  .personality-results {
    padding: 1rem;
  }

  .progress-section {
    grid-template-columns: 80px 1fr 80px;
    gap: 0.5rem;
  }

  .trait-card {
    padding: 1rem;
  }
}
</style>